import { type PayloadAction, createSlice } from "@reduxjs/toolkit"

import { generateSetPropReducer } from "../../../redux/shared/set-setate-prop.reducer.ts"
import { logout } from "../../LoginPage/redux/auth.thunks.ts"
import { PROPOSAL_STATUSES } from "../constants/proposal-statuses-keys.ts"
import type { Proposal, ProposalListItem } from "../types/types.ts"
import { addFetchingCases } from "./helpers.ts"
import { proposalsSliceName } from "./proposals.constants.ts"
import {
  approveProposal,
  cancelProposal,
  createProposal,
  duplicateProposal,
  emailProposalToClient,
  fetchProposal,
  fetchProposalInitialData,
  fetchProposals,
  generateProposal,
  getProposalFilterOptions,
  getProposalPreview,
  rejectProposal,
  saveProposal,
  sendProposalToApproval,
  submitProposalForReview,
  updateProposal
} from "./proposals.thunks.ts"
import { type ProposalsState } from "./proposals.types.ts"

// Initial State
const initialState: ProposalsState = {
  result: {
    count: 0,
    results: []
  },

  filters: {
    status: PROPOSAL_STATUSES.PENDING_TO_REVIEW,
    customer_id: null,
    lead_id: null
  },

  filterOptions: {
    status: []
  },

  proposal: null,

  fetching: false,
  fetchingList: false,
  listRequestId: null,
  saveFetching: false
}

// Slice
const proposalsSlice = createSlice({
  name: proposalsSliceName,
  initialState,
  reducers: {
    proposalsSetProp: generateSetPropReducer<ProposalsState>(),
    proposalsSetFilters: (
      state,
      action: PayloadAction<Partial<ProposalsState["filters"]>>
    ) => {
      state.filters = { ...state.filters, ...action.payload }
    }
  },
  extraReducers: (builder) => {
    addFetchingCases(builder, getProposalPreview)
    addFetchingCases(builder, createProposal)
    addFetchingCases(builder, generateProposal)
    addFetchingCases(builder, updateProposal)
    addFetchingCases(builder, saveProposal)
    addFetchingCases(builder, sendProposalToApproval)
    addFetchingCases(builder, submitProposalForReview)
    addFetchingCases(builder, approveProposal)
    addFetchingCases(builder, cancelProposal)
    addFetchingCases(builder, duplicateProposal)
    addFetchingCases(builder, rejectProposal)
    addFetchingCases(builder, fetchProposalInitialData, "fetching")

    builder
      // fetchProposals
      .addCase(fetchProposals.pending, (state, action) => {
        state.fetchingList = true
        state.listRequestId = action.meta.requestId
      })
      .addCase(fetchProposals.fulfilled, (state, action) => {
        if (
          state.listRequestId !== null &&
          state.listRequestId !== action.meta.requestId
        )
          return
        const data = action?.payload || []

        state.fetchingList = false
        state.result = { count: data?.length, results: data }
      })
      .addCase(fetchProposals.rejected, (state, action) => {
        if (
          state.listRequestId !== null &&
          state.listRequestId !== action.meta.requestId
        )
          return
        state.fetchingList = false
      })

      // fetchProposal
      .addCase(fetchProposal.pending, (state) => {
        state.fetching = true
      })
      .addCase(fetchProposal.fulfilled, (state, action) => {
        state.fetching = false
        state.proposal = action?.payload
      })
      .addCase(fetchProposal.rejected, (state) => {
        state.fetching = false
      })

      // emailProposalToClient
      .addCase(emailProposalToClient.pending, (state) => {
        state.saveFetching = true
      })
      .addCase(emailProposalToClient.fulfilled, (state, action) => {
        state.saveFetching = false
        if (state.proposal?.id === action.payload.id) {
          state.proposal = { ...state.proposal, ...action.payload } as Proposal
        }
        state.result.results = state.result.results.map((proposal) =>
          proposal.id === action.payload.id
            ? ({ ...proposal, ...action.payload } as ProposalListItem)
            : proposal
        )
      })
      .addCase(emailProposalToClient.rejected, (state) => {
        state.saveFetching = false
      })

      // fetchFilterOptions
      .addCase(getProposalFilterOptions.pending, (state) => {
        state.fetching = true
      })
      .addCase(getProposalFilterOptions.fulfilled, (state, action) => {
        state.fetching = false
        state.filterOptions = action?.payload
      })
      .addCase(getProposalFilterOptions.rejected, (state) => {
        state.fetching = false
      })

      .addCase(logout.fulfilled, () => {
        return initialState
      })
  }
})

export const { proposalsSetProp, proposalsSetFilters } = proposalsSlice.actions
export default proposalsSlice
